import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtMoney, fmtErr, fmtDate, fmtDateShort } from "../../api";
import Layout from "../../components/Layout";
import { Lightning, CalendarBlank } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function OwnerBook() {
  const [props_, setProps] = useState([]);
  const [propertyId, setPropertyId] = useState("");
  const [services, setServices] = useState([]);
  const [serviceName, setServiceName] = useState("");
  const [speed, setSpeed] = useState("flexible");
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");
  const [avail, setAvail] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const load = () => {
    api.get("/properties").then((r) => setProps(r.data));
    api.get("/availability").then((r) => setAvail(r.data)).catch(() => {});
    api.get("/owner/bookings").then((r) => setBookings(r.data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only load by design
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!propertyId) { setServices([]); setServiceName(""); return; }
    api.get(`/owner/properties/${propertyId}/services`).then((r) => { setServices(r.data); setServiceName(r.data[0]?.name || ""); }).catch(() => setServices([]));
  }, [propertyId]);

  const base = services.find((s) => s.name === serviceName)?.rate || 0;
  const priceFor = (sp) => sp === "express" ? base * (1 + (avail?.express_pct || 0) / 100)
    : sp === "rush" ? base + (avail?.rush_fee || 0) : base;
  const suggested = priceFor(speed);
  const openDays = (avail?.days || []).filter((d) => d.slots_left > 0 && d.date !== avail.today);

  const submit = async () => {
    if (!propertyId || !serviceName.trim()) return toast.error("Pick a property and a service");
    if (speed === "flexible" && !date) return toast.error("Pick an open day");
    setBusy(true);
    try {
      await api.post("/owner/book", { property_id: propertyId, service_name: serviceName, speed, requested_date: speed === "flexible" ? date : null, notes });
      toast.success("Request sent — your operator will confirm the date and price");
      setNotes(""); setDate(""); setSpeed("flexible");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };

  return (
    <Layout title="Book a service">
      {avail && !avail.enabled && (
        <div className="card-flat mb-4 border-l-4 border-l-primary p-4" data-testid="booking-disabled-note">
          <p className="font-semibold">Online booking isn't on right now</p>
          <p className="text-sm text-muted-foreground">Use "Request extra work" instead — your operator will quote it.</p>
          <button data-testid="goto-request-btn" onClick={() => nav("/owner/requests/new")} className="mt-3 h-12 w-full rounded-md bg-primary text-sm font-bold text-primary-foreground">Request extra work</button>
        </div>
      )}
      {avail?.enabled && (
        <div className="space-y-6" data-testid="booking-form">
          <section>
            <p className="label-caps mb-2">Which property?</p>
            <div className="space-y-2">
              {props_.map((p) => (
                <button key={p.id} data-testid={`book-property-${p.id}`} onClick={() => setPropertyId(p.id)}
                  className={`w-full rounded-md border p-4 text-left font-semibold transition-colors ${propertyId === p.id ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card"}`}>
                  {p.name} <span className="block text-xs font-normal opacity-80">{p.address}</span>
                </button>
              ))}
            </div>
          </section>

          {propertyId && (
            <section>
              <p className="label-caps mb-2">What service?</p>
              {services.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {services.map((s) => (
                    <button key={s.name} data-testid={`book-service-${s.name.replace(/\s+/g, "-")}`} onClick={() => setServiceName(s.name)}
                      className={`h-12 rounded-md border px-4 text-sm font-bold ${serviceName === s.name ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>
                      {s.name}{s.rate ? ` · ${fmtMoney(s.rate)}` : ""}
                    </button>
                  ))}
                </div>
              ) : (
                <input data-testid="book-service-custom" className="field-input w-full" placeholder="Service (e.g. Lawn care)" value={serviceName} onChange={(e) => setServiceName(e.target.value)} />
              )}
            </section>
          )}

          {propertyId && serviceName && (
            <section>
              <p className="label-caps mb-2">How fast do you need it?</p>
              <div className="space-y-2" data-testid="speed-picker">
                <button data-testid="speed-flexible" onClick={() => setSpeed("flexible")}
                  className={`w-full rounded-md border p-4 text-left ${speed === "flexible" ? "border-primary bg-primary/10" : "border-border"}`}>
                  <p className="font-bold">Flexible — pick an open day</p>
                  <p className="text-xs text-muted-foreground">Standard rate{base ? ` · ${fmtMoney(base)}` : ""}</p>
                </button>
                <button data-testid="speed-express" onClick={() => setSpeed("express")}
                  className={`w-full rounded-md border p-4 text-left ${speed === "express" ? "border-primary bg-primary/10" : "border-border"}`}>
                  <p className="font-bold">Express — next 2–3 days</p>
                  <p className="text-xs text-muted-foreground">+{avail.express_pct}% premium{base ? ` · ≈ ${fmtMoney(priceFor("express"))}` : ""}</p>
                </button>
                <button data-testid="speed-rush" onClick={() => avail.today_open && setSpeed("rush")} disabled={!avail.today_open}
                  className={`w-full rounded-md border p-4 text-left disabled:opacity-40 ${speed === "rush" ? "border-primary bg-primary/10" : "border-border"}`}>
                  <p className="font-bold">Rush — same day</p>
                  <p className="text-xs text-muted-foreground">{avail.today_open ? `+${fmtMoney(avail.rush_fee)} flat${base ? ` · ≈ ${fmtMoney(priceFor("rush"))}` : ""}` : "No rush capacity left today"}</p>
                </button>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">Times are always approximate — your operator confirms the exact day and price.</p>
            </section>
          )}

          {speed === "flexible" && serviceName && (
            <section>
              <p className="label-caps mb-2"><CalendarBlank size={14} className="mr-1 inline" /> Open days</p>
              {openDays.length === 0 && <p className="card-flat p-4 text-sm text-muted-foreground" data-testid="no-open-days">No open days in the next {avail.days.length ? "two weeks" : "while"} — try express or a manual request.</p>}
              <div className="grid grid-cols-2 gap-2" data-testid="open-days">
                {openDays.map((d) => (
                  <button key={d.date} data-testid={`book-day-${d.date}`} onClick={() => setDate(d.date)}
                    className={`rounded-md border p-3 text-left ${date === d.date ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>
                    <p className="text-sm font-bold">{d.weekday}</p>
                    <p className="text-xs opacity-80">{fmtDateShort(d.date)} · {d.slots_left} slot{d.slots_left === 1 ? "" : "s"}</p>
                  </button>
                ))}
              </div>
            </section>
          )}

          {propertyId && serviceName && (
            <section className="space-y-3">
              <textarea data-testid="book-notes" className="field-input h-20 w-full py-3" placeholder="Notes (gate code, areas to focus…)" value={notes} onChange={(e) => setNotes(e.target.value)} />
              {base > 0 && (
                <div className="card-flat flex items-center justify-between p-4" data-testid="price-preview">
                  <p className="text-sm font-semibold">Suggested price</p>
                  <p className="font-heading text-xl font-bold text-primary">{fmtMoney(Math.round(suggested * 100) / 100)}<span className="text-xs font-normal text-muted-foreground"> + tax</span></p>
                </div>
              )}
              <button data-testid="book-submit" onClick={submit} disabled={busy} className="btn-primary-big">
                {busy ? "Sending…" : "Request booking"}
              </button>
            </section>
          )}
        </div>
      )}

      {bookings.length > 0 && (
        <section className="mt-8">
          <p className="label-caps mb-3">My booking requests</p>
          <div className="space-y-2" data-testid="my-bookings">
            {bookings.map((b) => (
              <div key={b.id} className="card-flat p-4" data-testid={`booking-${b.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold">{b.service_name} · {b.property_name}</p>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-bold capitalize ${b.status === "approved" ? "bg-primary/10 text-primary" : b.status === "declined" ? "bg-destructive/10 text-destructive" : "bg-muted text-muted-foreground"}`} data-testid={`booking-status-${b.id}`}>{b.status}</span>
                </div>
                <div className="mt-1 space-y-0.5 text-sm text-muted-foreground" data-testid={`booking-detail-${b.id}`}>
                  <p>{b.speed === "rush" ? "Rush (same day)" : b.speed === "express" ? "Express (2–3 days)" : <>Requested: <span className="font-semibold text-foreground">{fmtDate(b.requested_date)}</span></>}</p>
                  {b.status === "approved" && b.scheduled_date && (
                    <p>Scheduled: <span className="font-semibold text-foreground">{fmtDate(b.scheduled_date)}</span>
                      {b.quoted_price != null && <> · Price: <span className="font-semibold text-foreground">{fmtMoney(b.quoted_price)}</span></>}</p>
                  )}
                  {b.status === "declined" && b.decline_reason && <p>Declined: {b.decline_reason}</p>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      <div className="mt-6 flex items-center gap-2 text-xs text-muted-foreground">
        <Lightning size={14} weight="bold" /> Express and rush slots cost extra — the premium is shown before you request.
      </div>
    </Layout>
  );
}
